import { useMutation, useQueryClient } from 'react-query';
import { httpClient } from 'src/utils/HttpClients';
import { AppContext, useAppState } from 'src/context';
import get from 'lodash/get';
import type { TBooking, TDeletePayload, TSubLocation } from 'src/types';
import { DATE_FORMAT } from 'src/constants';
import { useContext } from 'react';
import { getServerError } from 'src/utils/helpers/errors';

export const useDeleteBooking = () => {
    const queryClient = useQueryClient();
    const {
        values: { range, location },
    } = useAppState();
    const {
        actions: { handleCallNotification },
    } = useContext(AppContext);

    // Getting sub locations list
    const sData = queryClient.getQueryData(['sub-locations', location]);
    const params = {
        sublocation_id: get(sData, 'data', []).map((subLocation: TSubLocation) => subLocation.id),
        start_date: range.from.toFormat(DATE_FORMAT),
        end_date: range.to.toFormat(DATE_FORMAT),
    };

    const mutationFn = async ({ id, message }: TDeletePayload) => {
        const { data } = await httpClient.delete(`/booking/${id}`, {
            params: {
                message,
            },
        });
        return data;
    };

    const onMutate = ({ id }: TDeletePayload) => {
        const previousQuery = queryClient.getQueryData(['bookings', params]);

        queryClient.setQueryData<TBooking[]>(['bookings', params], (oldQuery) => {
            const bookings = oldQuery as TBooking[];

            return bookings.filter((booking) => booking.id !== id);
        });

        return () => queryClient.setQueryData(['bookings', params], previousQuery);
    };

    const onSuccess = async () => {
        await Promise.all([
            queryClient.invalidateQueries(['bookings', params]),
            queryClient.invalidateQueries(['policy-compliance'], { exact: false }),
        ]);
    };

    return useMutation(mutationFn, {
        onMutate,
        onSuccess,
        onError: (error, __, rollback) => {
            handleCallNotification({ message: getServerError(error), type: 'danger' });
            if (!rollback) {
                return;
            }
            rollback();
        },
    });
};
